import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  Switch,
  Paper,
  IconButton,
  Alert,
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { fetchData, fetchTeamTaskTemplates, saveTeamTaskTemplates } from "../../api/client";
import { useTeamBoard, useTeamBoardsList } from "../../hooks/useTeamBoards";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";

const DEFAULT_COLUMN_NAME = "A fazer";

const emptyTemplate = () => ({
  title: "",
  description: "",
  isRequired: false,
  defaultColumnName: DEFAULT_COLUMN_NAME,
  position: 0,
});

function resolveColumnName(preferred, columnNames) {
  if (!columnNames.length) return DEFAULT_COLUMN_NAME;
  if (preferred) {
    const match = columnNames.find(
      (name) => name.toLowerCase() === String(preferred).toLowerCase()
    );
    if (match) return match;
  }
  return columnNames[0];
}

const TeamTaskTemplatesSection = ({ tipoId, boardId = null, onBoardIdChange }) => {
  const { t } = useTranslation("teamTasks");
  const [templates, setTemplates] = useState([]);
  const [localBoardId, setLocalBoardId] = useState(boardId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingBoard, setSavingBoard] = useState(false);
  const [message, setMessage] = useState(null);

  const controlled = typeof onBoardIdChange === "function";
  const effectiveBoardId = controlled ? boardId : localBoardId;
  const hasLinkedBoard = effectiveBoardId != null && Number(effectiveBoardId) > 0;

  const { data: boardsData, isLoading: boardsLoading } = useTeamBoardsList({ workspace: 1 });
  const {
    data: linkedBoard,
    isLoading: linkedBoardLoading,
    isError: linkedBoardError,
  } = useTeamBoard(effectiveBoardId, {
    enabled: hasLinkedBoard,
  });

  useEffect(() => {
    void ensureNamespaceLoaded("teamTasks");
  }, []);

  const boardColumns = useMemo(() => {
    const cols = linkedBoard?.columns || [];
    return cols
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || (a.id ?? 0) - (b.id ?? 0))
      .map((col) => ({
        id: col.id,
        name: col.name,
        isDone: Boolean(col.isDone),
      }))
      .filter((col) => col.name);
  }, [linkedBoard]);

  const columnNames = useMemo(() => boardColumns.map((col) => col.name), [boardColumns]);

  const workspaceBoards = useMemo(() => {
    const list = (Array.isArray(boardsData) ? boardsData : boardsData?.boards || []).filter(
      (b) => !b.leadId && !b.spaceId
    );
    if (
      effectiveBoardId != null &&
      !list.some((b) => Number(b.id) === Number(effectiveBoardId))
    ) {
      return [
        ...list,
        { id: Number(effectiveBoardId), name: t("tipoBoardLinkFallback", { id: effectiveBoardId }) },
      ];
    }
    return list;
  }, [boardsData, effectiveBoardId, t]);

  const selectedBoard = useMemo(
    () => workspaceBoards.find((b) => Number(b.id) === Number(effectiveBoardId)) || null,
    [workspaceBoards, effectiveBoardId]
  );

  useEffect(() => {
    if (!controlled) setLocalBoardId(boardId);
  }, [boardId, controlled]);

  useEffect(() => {
    if (!tipoId) return;
    let cancelled = false;
    setLoading(true);
    fetchTeamTaskTemplates(tipoId)
      .then((data) => {
        if (cancelled) return;
        const rows = data.templates || [];
        setTemplates(rows.length ? rows : [emptyTemplate()]);
        const nextBoardId = data.boardId != null ? Number(data.boardId) : null;
        if (controlled) {
          if (boardId == null && nextBoardId != null) {
            onBoardIdChange(nextBoardId);
          }
        } else {
          setLocalBoardId(nextBoardId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([emptyTemplate()]);
          if (!controlled) setLocalBoardId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoId]);

  // Sem painel: força "A fazer". Com painel: alinha nomes às colunas reais.
  useEffect(() => {
    setTemplates((prev) =>
      prev.map((row) => {
        if (!hasLinkedBoard) {
          return row.defaultColumnName === DEFAULT_COLUMN_NAME
            ? row
            : { ...row, defaultColumnName: DEFAULT_COLUMN_NAME };
        }
        if (!columnNames.length) return row;
        const next = resolveColumnName(row.defaultColumnName, columnNames);
        return next === row.defaultColumnName ? row : { ...row, defaultColumnName: next };
      })
    );
  }, [hasLinkedBoard, columnNames]);

  const updateRow = (index, patch) => {
    setTemplates((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () =>
    setTemplates((prev) => [
      ...prev,
      {
        ...emptyTemplate(),
        defaultColumnName: hasLinkedBoard
          ? resolveColumnName(DEFAULT_COLUMN_NAME, columnNames)
          : DEFAULT_COLUMN_NAME,
      },
    ]);

  const removeRow = (index) => {
    setTemplates((prev) => (prev.length <= 1 ? [emptyTemplate()] : prev.filter((_, i) => i !== index)));
  };

  const persistBoardId = async (nextId) => {
    if (!tipoId) return;
    setSavingBoard(true);
    setMessage(null);
    try {
      await fetchData(`/types/${tipoId}`, "PUT", {
        id: tipoId,
        id_team_board: nextId,
      });
      setMessage({ severity: "success", text: t("tipoBoardLinkSaved") });
    } catch {
      setMessage({ severity: "error", text: t("tipoBoardLinkSaveError") });
      throw new Error("board_save_failed");
    } finally {
      setSavingBoard(false);
    }
  };

  const handleBoardChange = async (_e, value) => {
    const previousId =
      effectiveBoardId != null && Number(effectiveBoardId) > 0 ? Number(effectiveBoardId) : null;
    const nextId = value?.id != null ? Number(value.id) : null;
    const normalized = nextId != null && nextId > 0 ? nextId : null;
    if (controlled) onBoardIdChange(normalized);
    else setLocalBoardId(normalized);
    try {
      await persistBoardId(normalized);
    } catch {
      if (controlled) onBoardIdChange(previousId);
      else setLocalBoardId(previousId);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = templates
        .filter((row) => String(row.title || "").trim())
        .map((row, i) => ({
          title: row.title.trim(),
          description: row.description || null,
          isRequired: Boolean(row.isRequired),
          defaultColumnName: hasLinkedBoard
            ? resolveColumnName(row.defaultColumnName, columnNames)
            : DEFAULT_COLUMN_NAME,
          defaultAssigneeId: row.defaultAssigneeId || null,
          position: i,
        }));
      try {
        await persistBoardId(effectiveBoardId);
      } catch {
        // continue trying templates
      }
      const saved = await saveTeamTaskTemplates(tipoId, payload, { boardId: effectiveBoardId });
      setTemplates(saved.templates.length ? saved.templates : [emptyTemplate()]);
      const savedBoardId = saved.boardId != null ? Number(saved.boardId) : null;
      if (controlled) onBoardIdChange(savedBoardId);
      else setLocalBoardId(savedBoardId);
      setMessage({ severity: "success", text: t("templateSaved") });
    } catch {
      setMessage({ severity: "error", text: t("templateSaveError") });
    } finally {
      setSaving(false);
    }
  };

  if (!tipoId) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t("templateSectionTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t("templateSectionHint")}
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 1 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t("tipoBoardLinkTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {t("tipoBoardLinkHint")}
        </Typography>
        <Autocomplete
          size="small"
          options={workspaceBoards}
          loading={boardsLoading || savingBoard}
          value={selectedBoard}
          onChange={handleBoardChange}
          disabled={savingBoard}
          getOptionLabel={(opt) => opt?.name || `#${opt?.id}`}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("tipoBoardLinkLabel")}
              placeholder={t("tipoBoardLinkPlaceholder")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {boardsLoading || savingBoard ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Paper>

      <Stack spacing={1.5}>
        {templates.map((row, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  label={t("templateTitle")}
                  value={row.title || ""}
                  onChange={(e) => updateRow(index, { title: e.target.value })}
                  fullWidth
                />
                <IconButton size="small" onClick={() => removeRow(index)} aria-label={t("delete")}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                size="small"
                label={t("cardDescription")}
                value={row.description || ""}
                onChange={(e) => updateRow(index, { description: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                {hasLinkedBoard ? (
                  linkedBoardError ? (
                    <Typography variant="caption" color="error" sx={{ flex: 1 }}>
                      {t("templateDefaultColumnLoadError")}
                    </Typography>
                  ) : linkedBoardLoading || !columnNames.length ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minHeight: 40 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        {t("templateDefaultColumnLoading")}
                      </Typography>
                    </Stack>
                  ) : (
                    <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                      <InputLabel id={`template-column-${index}`}>{t("templateDefaultColumn")}</InputLabel>
                      <Select
                        labelId={`template-column-${index}`}
                        label={t("templateDefaultColumn")}
                        value={resolveColumnName(row.defaultColumnName, columnNames)}
                        onChange={(e) => updateRow(index, { defaultColumnName: e.target.value })}
                      >
                        {boardColumns.map((col) => (
                          <MenuItem key={col.id || col.name} value={col.name}>
                            {col.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                ) : null}
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(row.isRequired)}
                      onChange={(e) => updateRow(index, { isRequired: e.target.checked })}
                    />
                  }
                  label={t("cardRequired")}
                />
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={loading}>
          {t("templateAdd")}
        </Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || loading}>
          {t("templateSave")}
        </Button>
      </Stack>
    </Box>
  );
};

export default TeamTaskTemplatesSection;
